import { inject, Injectable } from '@angular/core';
import { FilterBy } from '@shared/sync/filter-by.model';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { Task } from '../models/task.model';
import { InvoiceMapper } from '../mappers/invoice.mapper';
import { GetTaskInPaginationResponse } from '../models/get-task-in-pagination-response.model';
import { PrintSettings } from '@shared/settings/print-settings.model';
import { StatusHistoryService } from '@modules/status-history/services/status-hsitory.service';
import { HistoryStatus } from '../enums/history-status.enum';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private readonly _serviceKey = 'kfmInvoice';
  private readonly _blazorService = inject(HsBlazorService);
  private readonly _mapper = new InvoiceMapper();
  private readonly _statusHistoryService = inject(StatusHistoryService);

  public async getAllOpenInvoices(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
  ): Promise<IPaginatedItemsModel<Task>> {
    const invoiceResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetOpen',
      {
        sortBy: sortBy,
        filterBy: filterBy,
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    );

    if (invoiceResult) {
      const result = invoiceResult as Record<string, unknown>;
      const invoices: IPaginatedItemsModel<Task> = {
        count: Number(result['count']),
        data: (result['data'] as Task[]).map((a: Task) =>
          this._mapper.invoiceMap(a),
        ),
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
      };

      return invoices;
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async getAllBookedAndCancelledInvoices(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
  ): Promise<IPaginatedItemsModel<Task>> {
    const invoiceResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetBookedAndCancelled',
      {
        sortBy: sortBy,
        filterBy: filterBy,
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    );

    if (invoiceResult) {
      const result = invoiceResult as Record<string, unknown>;
      const invoices: IPaginatedItemsModel<Task> = {
        count: Number(result['count']),
        data: (result['data'] as Task[]).map((a: Task) =>
          this._mapper.invoiceMap(a),
        ),
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
      };

      return invoices;
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async getInPagination(
    invoiceId: string,
    from: Date,
    to: Date,
    pageSize = 10,
    showBooked = false,
    showCanceled = false,
    sortBy: SortBy | null = null,
  ): Promise<GetTaskInPaginationResponse | undefined> {
    const invoiceResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetInPagination',
      {
        invoiceId: invoiceId,
        pageSize: pageSize,
        from: from,
        to: to,
        showBooked: showBooked,
        showCanceled: showCanceled,
        sortBy: sortBy,
      },
    );

    if (invoiceResult) {
      const result = invoiceResult as Record<string, unknown>;
      const invoices: GetTaskInPaginationResponse = {
        count: Number(result['count']),
        data: (result['data'] as Task[]).map((a: Task) =>
          this._mapper.invoiceMap(a),
        ),
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
        from: new Date(String(result['from'])),
        to: new Date(String(result['to'])),
      };

      return invoices;
    }

    return undefined;
  }

  public async getById(id: string): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetByIds',
      { ids: [id] },
    );

    if (result) {
      const invoices = result as Record<string, unknown>;
      const data = invoices['invoices'] as Task[];

      if (data && data.length > 0) return this._mapper.invoiceMap(data[0]);
    }

    return undefined;
  }

  public async create(customerId?: string): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Create',
      { customerId: customerId },
    );

    if (result) {
      const invoice = (result as Record<string, unknown>)['invoice'] as Task;
      return this._mapper.invoiceMap(invoice);
    }

    return undefined;
  }

  public async createWithItems(
    items: { itemId: string; quantity: number }[],
  ): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'CreateWithItems',
      { invoiceItems: items },
    );

    if (result) {
      const invoice = (result as Record<string, unknown>)['invoice'] as Task;
      return this._mapper.invoiceMap(invoice);
    }

    return undefined;
  }

  public async update(value: Task): Promise<boolean> {
    const request = this._mapper.mapInvoiceRequest(value);
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Put',
      request,
    );

    return !!result;
  }

  public async delete(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Delete',
      { id: id },
    );

    const deleted = (result as Record<string, unknown>)['deleted'];
    return !!deleted;
  }

  /**
   * Cancels an invoice by:
   * 1. Finding the original (non-storno) booking linked via documentIdentifier.
   * 2. Fetching the current invoice so we can return it with the updated status.
   * 3. Calling CancelBooking to create a reversal entry.
   * 4. Adding a STATUS_STORNIERT status history entry to the invoice.
   * 5. Returning the invoice with STATUS_STORNIERT prepended to statusHistory so
   *    the list can be updated locally without waiting for a WASM reload.
   */
  public async cancel(id: string): Promise<Task | null> {
    try {
      const activeBooking = await this.getBookingForInvoice(id);
      if (!activeBooking) {
        console.error(
          '[InvoiceService] Keine aktive Buchung gefunden für Rechnung:',
          id,
        );
        return null;
      }

      const bookingId = String(activeBooking['uId'] ?? '');
      if (!bookingId) return null;

      // Fetch the current invoice so we can return it with the updated status
      const invoice = await this.getById(id);
      if (!invoice) return null;

      // Create the Storno reversal booking on the financialBooking module
      await this._blazorService.invokeService(
        'financialBooking',
        'CancelBooking',
        {
          bookingId,
        },
      );

      // Set STATUS_STORNIERT on the invoice so the UI and list reflect the cancellation
      const stornoEntry = this._statusHistoryService.getTemplate(
        '',
        HistoryStatus.Storniert,
        id,
      );
      await this._statusHistoryService.create(stornoEntry);

      // Prepend the storno entry locally so the caller gets an up-to-date invoice
      // without waiting for WASM to propagate the write.
      const updatedStatusHistory = [
        stornoEntry,
        ...(invoice.statusHistory ?? []),
      ];
      return { ...invoice, statusHistory: updatedStatusHistory };
    } catch (err) {
      console.error('[InvoiceService] Fehler beim Stornieren:', err);
      return null;
    }
  }

  /**
   * Returns the first original (non-storno) booking linked to the given invoice
   * via its documentIdentifier field, BUT only if that booking has NOT yet been
   * reversed. A booking is considered reversed when a storno entry (bookingType 2)
   * exists that references it via parentBookingUId.
   * Returns null when no active (unreversed) booking is found.
   */
  public async getBookingForInvoice(
    invoiceId: string,
  ): Promise<Record<string, unknown> | null> {
    try {
      const result = await this._blazorService.invokeService(
        'financialBooking',
        'Get',
        {
          pageIndex: 0,
          pageSize: 10,
          sortBy: null,
          filterBy: {
            filterStatement: 'return i.documentIdentifier === p[0]',
            parameters: [invoiceId],
          },
        },
      );
      const bookings =
        (result as { data: Record<string, unknown>[] })?.data ?? [];

      // Original booking: not a storno reversal entry
      const original = bookings.find((b) => (b['bookingType'] as number) !== 2);
      if (!original) return null;

      // Check if a storno reversal already references this original booking
      const originalId = String(original['uId'] ?? '');
      const hasReversal = bookings.some(
        (b) =>
          (b['bookingType'] as number) === 2 &&
          String(b['parentBookingUId'] ?? '') === originalId,
      );

      return hasReversal ? null : original;
    } catch {
      return null;
    }
  }

  public async book(
    value: Task,
    printSettings: PrintSettings,
  ): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Book',
      {
        id: value.uId,
        printSettings: printSettings,
      },
    );

    if (result) {
      const invoice = (result as Record<string, unknown>)['invoice'] as Task;
      return this._mapper.invoiceMap(invoice);
    }

    return undefined;
  }
}
